import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Table, 
  DatePicker, 
  Space, 
  Button,
  Divider,
  Spin,
  message
} from 'antd'
import { 
  BookOutlined, 
  FileTextOutlined, 
  SearchOutlined,
  UserOutlined,
  BarChartOutlined,
  CalendarOutlined,
  DownloadOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { apiRequest } from '../../utils/api'

const { RangePicker } = DatePicker

function SystemStats() {
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalCategories: 0,
    totalPages: 0,
    totalSearches: 0
  })
  
  const [searchStats, setSearchStats] = useState([])
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ])
  
  // 获取统计数据
  useEffect(() => {
    fetchStats()
    fetchSearchStats()
  }, [])
  
  const fetchStats = async () => {
    setLoading(true)
    try {
      const response = await apiRequest('/admin/stats/summary')
      const data = await response.json()
      
      if (response.ok) {
        setStats(data.stats || {
          totalBooks: 0,
          totalCategories: 0,
          totalPages: 0,
          totalSearches: 0
        })
      }
    } catch (error) {
      console.error('获取统计信息失败:', error)
      message.error('获取统计信息失败')
    } finally {
      setLoading(false)
    }
  }
  
  const fetchSearchStats = async () => {
    try {
      const [startDate, endDate] = dateRange
      const response = await apiRequest(`/admin/stats/searches?start=${startDate.format('YYYY-MM-DD')}&end=${endDate.format('YYYY-MM-DD')}`)
      const data = await response.json()
      
      if (response.ok) {
        setSearchStats(data.stats || [])
      }
    } catch (error) {
      console.error('获取搜索统计失败:', error)
      message.error('获取搜索统计失败')
    }
  }
  
  const handleDateChange = (dates) => {
    setDateRange(dates)
  }
  
  const handleSearch = () => {
    fetchSearchStats()
  }
  
  const exportStats = () => {
    message.info('导出功能开发中...')
  }
  
  const searchColumns = [
    {
      title: '搜索关键词',
      dataIndex: 'query',
      key: 'query',
    },
    {
      title: '搜索次数',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: '平均结果数',
      dataIndex: 'avg_results',
      key: 'avg_results',
      render: (avg) => Math.round(avg),
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
  ]
  
  return (
    <div className="system-stats">
      <Card className="management-card">
        <div className="stats-header">
          <Space size="middle">
            <BarChartOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <h2>系统统计</h2>
          </Space>
        </div>
        
        <Divider />
        
        {/* 统计概览 */}
        <Spin spinning={loading}>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总书籍数" 
                  value={stats.totalBooks} 
                  prefix={<BookOutlined />} 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总分类数" 
                  value={stats.totalCategories} 
                  prefix={<FileTextOutlined />} 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总页数" 
                  value={stats.totalPages} 
                  prefix={<FileTextOutlined />} 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="总搜索次数" 
                  value={stats.totalSearches} 
                  prefix={<SearchOutlined />} 
                />
              </Card>
            </Col>
          </Row>
        </Spin>
        
        <Divider />
        
        {/* 搜索统计 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <CalendarOutlined />
              <span>搜索统计时间范围:</span>
              <RangePicker 
                value={dateRange}
                onChange={handleDateChange}
                allowClear={false}
              />
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                查询
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={exportStats}
              >
                导出数据
              </Button>
            </Space>
          </div>
          
          <Table
            dataSource={searchStats}
            columns={searchColumns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
        
        {/* 系统信息 */}
        <Card size="small">
          <h3>系统信息</h3>
          <Row gutter={16}>
            <Col span={8}>
              <p><strong>部署平台:</strong> Cloudflare</p>
            </Col>
            <Col span={8}>
              <p><strong>数据库:</strong> D1 (SQLite)</p>
            </Col>
            <Col span={8}>
              <p><strong>存储:</strong> R2 对象存储</p>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <p><strong>前端框架:</strong> React + Vite</p>
            </Col>
            <Col span={8}>
              <p><strong>UI框架:</strong> Ant Design</p>
            </Col>
            <Col span={8}>
              <p><strong>API:</strong> Cloudflare Workers</p>
            </Col>
          </Row>
        </Card>
      </Card>
    </div>
  )
}

export default SystemStats